import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./CPDropdown.css";

const MENU_GAP_PX = 6;
const MENU_MAX_HEIGHT_PX = 280;
const VIEWPORT_PADDING_PX = 8;
const MENU_Z_INDEX = 10060;

function normalizeOptionValue(value) {
  if (value == null) return "";
  return String(value);
}

function computeMenuPosition(triggerRect) {
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const spaceBelow = viewportHeight - triggerRect.bottom - VIEWPORT_PADDING_PX;
  const spaceAbove = triggerRect.top - VIEWPORT_PADDING_PX;
  const preferredMax = Math.min(MENU_MAX_HEIGHT_PX, viewportHeight * 0.5);
  const openAbove =
    spaceBelow < Math.min(preferredMax, 160) && spaceAbove > spaceBelow;
  const available = openAbove ? spaceAbove : spaceBelow;
  const maxHeight = Math.max(
    96,
    Math.min(preferredMax, available - MENU_GAP_PX),
  );

  let left = triggerRect.left;
  const width = triggerRect.width;
  if (left + width > viewportWidth - VIEWPORT_PADDING_PX) {
    left = Math.max(
      VIEWPORT_PADDING_PX,
      viewportWidth - VIEWPORT_PADDING_PX - width,
    );
  }
  if (left < VIEWPORT_PADDING_PX) {
    left = VIEWPORT_PADDING_PX;
  }

  if (openAbove) {
    return {
      position: "fixed",
      top: Math.max(
        VIEWPORT_PADDING_PX,
        triggerRect.top - MENU_GAP_PX - maxHeight,
      ),
      left,
      width,
      maxHeight,
      zIndex: MENU_Z_INDEX,
      placement: "above",
    };
  }

  return {
    position: "fixed",
    top: triggerRect.bottom + MENU_GAP_PX,
    left,
    width,
    maxHeight,
    zIndex: MENU_Z_INDEX,
    placement: "below",
  };
}

/**
 * Shared CrewPilot dropdown — canonical closed + open UI.
 *
 * Public API:
 * - props: className, label, value, onChange(value), options[{value,label}],
 *   placeholder, includePlaceholder (=true), required, disabled,
 *   voiceFieldId, voiceActive
 * - ref: { focus(), open() }
 *
 * Open menu is a custom listbox portaled to document.body (fixed positioning)
 * so overflow:hidden ancestors (e.g. Start Session .kpi-card) cannot clip it.
 */
export const CPDropdown = forwardRef(function CPDropdown(
  {
    className = "",
    label,
    value,
    onChange,
    options = [],
    placeholder = "Select an option",
    includePlaceholder = true,
    required = false,
    disabled = false,
    voiceFieldId,
    voiceActive = false,
  },
  ref,
) {
  const listId = useId();
  const labelId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);

  const normalizedValue = normalizeOptionValue(value);
  const normalizedOptions = useMemo(
    () =>
      (options || []).map((option) => ({
        value: normalizeOptionValue(option?.value),
        label: String(option?.label ?? option?.value ?? ""),
      })),
    [options],
  );

  const callerHasEmptyOption = useMemo(
    () => normalizedOptions.some((option) => option.value === ""),
    [normalizedOptions],
  );

  // Inject empty placeholder unless caller already supplied value:"" or opted out.
  const menuOptions = useMemo(() => {
    if (callerHasEmptyOption || !includePlaceholder) {
      return normalizedOptions;
    }
    return [{ value: "", label: placeholder }, ...normalizedOptions];
  }, [
    callerHasEmptyOption,
    includePlaceholder,
    normalizedOptions,
    placeholder,
  ]);

  const selectedOption = useMemo(
    () =>
      menuOptions.find((option) => option.value === normalizedValue) || null,
    [menuOptions, normalizedValue],
  );

  const injectedPlaceholderSelected =
    includePlaceholder &&
    !callerHasEmptyOption &&
    selectedOption?.value === "";

  const displayLabel = selectedOption
    ? injectedPlaceholderSelected
      ? placeholder
      : selectedOption.label
    : placeholder;

  const showingPlaceholder =
    !selectedOption || injectedPlaceholderSelected;

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    setMenuStyle(null);
  }, []);

  const updateMenuPosition = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) return;
    const next = computeMenuPosition(trigger.getBoundingClientRect());
    setMenuStyle(next);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    const selectedIdx = menuOptions.findIndex(
      (option) => option.value === normalizedValue,
    );
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
    setOpen(true);
  }, [disabled, menuOptions, normalizedValue]);

  const toggle = useCallback(() => {
    if (open) {
      close();
    } else {
      openMenu();
    }
  }, [close, open, openMenu]);

  const selectIndex = useCallback(
    (index) => {
      const option = menuOptions[index];
      if (!option) return;
      onChange?.(option.value);
      close();
      buttonRef.current?.focus();
    },
    [close, menuOptions, onChange],
  );

  useImperativeHandle(ref, () => ({
    focus: () => buttonRef.current?.focus(),
    open: () => {
      openMenu();
      buttonRef.current?.focus();
    },
  }));

  useEffect(() => {
    if (!voiceActive) return;
    buttonRef.current?.focus();
  }, [voiceActive]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();

    const handleReposition = () => updateMenuPosition();
    window.addEventListener("resize", handleReposition);
    // Capture scroll from nested overflow containers too.
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, updateMenuPosition, menuOptions.length]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      close();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [close, open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const optionEl = listRef.current?.querySelector(
      `[data-option-index="${activeIndex}"]`,
    );
    if (typeof optionEl?.scrollIntoView === "function") {
      optionEl.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open]);

  const handleButtonKeyDown = (event) => {
    if (disabled) return;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ":
        event.preventDefault();
        if (!open) {
          openMenu();
          if (event.key === "ArrowUp") {
            setActiveIndex(Math.max(menuOptions.length - 1, 0));
          }
        } else if (event.key === "Enter" || event.key === " ") {
          if (activeIndex >= 0) {
            selectIndex(activeIndex);
          } else {
            close();
          }
        } else if (event.key === "ArrowDown") {
          setActiveIndex((prev) =>
            Math.min((prev < 0 ? -1 : prev) + 1, menuOptions.length - 1),
          );
        } else if (event.key === "ArrowUp") {
          setActiveIndex((prev) => Math.max((prev < 0 ? 0 : prev) - 1, 0));
        }
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      case "Home":
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (open) {
          event.preventDefault();
          setActiveIndex(Math.max(menuOptions.length - 1, 0));
        }
        break;
      case "Tab":
        if (open) close();
        break;
      default:
        break;
    }
  };

  const menu =
    open && menuStyle && typeof document !== "undefined"
      ? createPortal(
          <ul
            ref={listRef}
            id={listId}
            className="cp-dropdown-menu"
            role="listbox"
            tabIndex={-1}
            data-cp-dropdown-menu="true"
            data-placement={menuStyle.placement}
            aria-labelledby={label ? labelId : undefined}
            style={{
              position: menuStyle.position,
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
              maxHeight: menuStyle.maxHeight,
              zIndex: menuStyle.zIndex,
            }}
            onKeyDown={handleButtonKeyDown}
          >
            {menuOptions.length === 0 ? (
              <li
                className="cp-dropdown-option cp-dropdown-option--empty"
                role="presentation"
              >
                No options
              </li>
            ) : (
              menuOptions.map((option, index) => {
                const selected = option.value === normalizedValue;
                const active = index === activeIndex;
                return (
                  <li
                    key={`${option.value}::${index}`}
                    id={`${listId}-opt-${index}`}
                    role="option"
                    data-option-index={index}
                    aria-selected={selected}
                    className={`cp-dropdown-option${
                      selected ? " cp-dropdown-option--selected" : ""
                    }${active ? " cp-dropdown-option--active" : ""}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      // Prevent button blur-before-click from closing first.
                      event.preventDefault();
                    }}
                    onClick={() => selectIndex(index)}
                  >
                    {option.label}
                  </li>
                );
              })
            )}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={`cp-input-group cp-dropdown-field ${className}${
        voiceActive ? " cp-input-group--voice-active" : ""
      }${open ? " cp-dropdown-field--open" : ""}`}
      data-voice-field={voiceFieldId}
    >
      {label ? (
        <label
          id={labelId}
          className="cp-input-label"
          onClick={() => {
            if (!disabled) buttonRef.current?.focus();
          }}
        >
          {label}
        </label>
      ) : null}

      <div className="cp-dropdown-control">
        <button
          ref={buttonRef}
          type="button"
          className={`cp-dropdown${
            voiceActive || open ? " cp-dropdown--voice-active" : ""
          }${showingPlaceholder ? " cp-dropdown--placeholder" : ""}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-required={required || undefined}
          aria-activedescendant={
            open && activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined
          }
          aria-labelledby={label ? labelId : undefined}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          onClick={toggle}
          onKeyDown={handleButtonKeyDown}
        >
          <span className="cp-dropdown__value">{displayLabel}</span>
          <span className="cp-dropdown__chevron" aria-hidden="true" />
        </button>
      </div>

      {menu}
    </div>
  );
});
